/**
 * Оконная подгрузка страниц под виртуализированную таблицу.
 *
 * Сервер отдаёт `{page:{limit,offset,total}, items}`. Хук держит загруженные
 * строки по АБСОЛЮТНОМУ индексу и докачивает страницы по мере прокрутки.
 * Смена контекста/фильтров — это новое поколение: старые ответы, пришедшие
 * позже, отбрасываются и не подмешивают чужие строки в новую выборку.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "./client";
import type { ScopeOut } from "./client";

export interface PageResponse<T> {
  page: { limit: number; offset: number; total: number };
  items: T[];
  /**
   * Контекст ответа. Нужен ради `scope`: короткий список обязан объяснять, он
   * короток потому, что строк нет, или потому, что выдача сужена до хозяйств
   * аккаунта. Поле необязательное — не всякая страница его отдаёт.
   */
  context?: { scope?: ScopeOut | null };
}

export interface WindowedCollection<T> {
  total: number | null;
  rows: ReadonlyMap<number, T>;
  error: ApiError | null;
  loading: boolean;
  /** Сколько строк реально загружено (для честной подписи под таблицей). */
  loaded: number;
  /** Область чтения, объявленная последней принятой страницей. */
  scope: ScopeOut | null;
  ensureRange: (start: number, end: number) => void;
  reload: () => void;
}

export function useWindowedCollection<T>(
  fetchPage: (offset: number, limit: number) => Promise<PageResponse<T>>,
  resetKey: string,
  pageSize = 200,
): WindowedCollection<T> {
  const [total, setTotal] = useState<number | null>(null);
  const [rows, setRows] = useState<ReadonlyMap<number, T>>(new Map());
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState(0);
  const [scope, setScope] = useState<ScopeOut | null>(null);

  const generation = useRef(0);
  const requested = useRef<Set<number>>(new Set());
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;

  const loadPage = useCallback(
    (page: number) => {
      if (requested.current.has(page)) return;
      const mine = generation.current;
      requested.current.add(page);
      setPending((value) => value + 1);

      fetchRef
        .current(page * pageSize, pageSize)
        .then((response) => {
          if (mine !== generation.current) return;
          setTotal(response.page.total);
          setScope(response.context?.scope ?? null);
          setRows((previous) => {
            const next = new Map(previous);
            response.items.forEach((item, index) => {
              next.set(response.page.offset + index, item);
            });
            return next;
          });
          setError(null);
        })
        .catch((cause: unknown) => {
          if (mine !== generation.current) return;
          // Страницу можно попробовать снова: неудачная попытка не «залипает».
          requested.current.delete(page);
          setError(
            cause instanceof ApiError
              ? cause
              : new ApiError("unknown_error", String(cause), 0),
          );
        })
        .finally(() => {
          if (mine !== generation.current) return;
          setPending((value) => Math.max(0, value - 1));
        });
    },
    [pageSize],
  );

  useEffect(() => {
    generation.current += 1;
    requested.current = new Set();
    setRows(new Map());
    setTotal(null);
    setError(null);
    setPending(0);
    setScope(null);
    loadPage(0);
  }, [resetKey, loadPage]);

  const ensureRange = useCallback(
    (start: number, end: number) => {
      const firstPage = Math.max(0, Math.floor(start / pageSize));
      const lastPage = Math.floor(end / pageSize);
      for (let page = firstPage; page <= lastPage; page += 1) loadPage(page);
    },
    [loadPage, pageSize],
  );

  const reload = useCallback(() => {
    generation.current += 1;
    requested.current = new Set();
    setRows(new Map());
    setTotal(null);
    setError(null);
    setPending(0);
    setScope(null);
    loadPage(0);
  }, [loadPage]);

  return {
    total,
    rows,
    error,
    loading: pending > 0,
    loaded: rows.size,
    scope,
    ensureRange,
    reload,
  };
}
